import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { trackCta, trackDownload } from "../lib/track";
import { TOOLS } from "../tools";

/**
 * The trusted chunk html (built from our own repo by build-content.mjs).
 *
 * Two things React has to do to markup it did not render: fill each
 * `.tool-slot` from the TOOLS registry (portals into the placeholder, so the
 * slot is untouched when the name is unknown) and count CTA clicks with one
 * delegated handler. Links to /downloads/ are counted the same way.
 */
export default function ArticleBody({ html, slug, lang }) {
  const ref = useRef(null);
  const [slots, setSlots] = useState([]);
  // Same object every render: a new one makes React rewrite the node's
  // innerHTML, which detaches the .tool-slot elements the tools are portaled
  // into (the tool then renders into a node that is no longer on the page).
  const inner = useMemo(() => ({ __html: html }), [html]);

  useEffect(() => {
    const found = [...ref.current.querySelectorAll(".tool-slot[data-tool]")]
      .map((el) => ({ el, Tool: TOOLS[el.dataset.tool] }))
      .filter((s) => s.Tool);
    setSlots(found);
  }, [html]);

  const onClick = (e) => {
    const link = e.target.closest?.('[data-cta="content"] a');
    if (link) trackCta(slug, "article");
    const file = e.target.closest?.('a[href^="/downloads/"]');
    if (file) trackDownload(file.getAttribute("href").split("/").pop());
  };

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        ref={ref}
        className="prose"
        lang={lang === "hi-IN" ? "hi" : undefined}
        onClick={onClick}
        dangerouslySetInnerHTML={inner}
      />
      {slots.map(({ el, Tool }) =>
        createPortal(
          <Suspense fallback={null}>
            <Tool />
          </Suspense>,
          el,
          el.dataset.tool
        )
      )}
    </>
  );
}
